/**
 * Homepage domain — cache invalidation registry.
 *
 * Defines which queries every wrapped homepage mutation should
 * invalidate on success. Consumed by `hooks.ts` via
 * `useInvalidatingMutation`. The compiler enforces that every entry in
 * `HomepageMutationId` has a registry entry, so adding a new mutation
 * without declaring its invalidations is a build error.
 */

import type { QueryKey } from '@tanstack/react-query'

import type { InvalidationRule } from '../invalidation/useInvalidatingMutation'
import {
  listHomepagePinsQueryKey,
  listHomepageRecentsQueryKey,
} from '../generated/hooks/homepageController'

// ── Mutation identifiers ────────────────────────────────────────────

/**
 * Discriminator union of every wrapped homepage mutation. Adding a new
 * mutation requires adding a member here and a corresponding entry in
 * `invalidationRegistry`.
 */
export type HomepageMutationId =
  | 'createHomepagePin'
  | 'deleteHomepagePin'
  | 'reorderHomepagePins'
  | 'recordHomepageVisit'

// ── Shared invalidation set ─────────────────────────────────────────

/**
 * Query keys that pin mutations should invalidate.
 */
export function commonPinInvalidations(): QueryKey[] {
  return [listHomepagePinsQueryKey()]
}

// ── Registry ────────────────────────────────────────────────────────

/**
 * Typed registry mapping every homepage mutation to the queries it
 * should invalidate on success.
 */
export const invalidationRegistry: Record<
  HomepageMutationId,
  InvalidationRule<any>
> = {
  createHomepagePin: { invalidates: () => commonPinInvalidations() },
  deleteHomepagePin: { invalidates: () => commonPinInvalidations() },
  reorderHomepagePins: { invalidates: () => commonPinInvalidations() },
  recordHomepageVisit: { invalidates: () => [listHomepageRecentsQueryKey()] },
}
